import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  FaChevronCircleLeft,
  FaChevronCircleRight,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { Navbar, Header, Footer } from "../components";
import Loader from "../components/Loader";
import ErrorPage from "./ErrorPage";
import customAxios from "../utils/customAxios";
import { formatDate } from "../utils/helper";

import { styles } from "../styles";

const PenghargaanPage = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const [pagination, setPagination] = useState(1);
  const [meta, setMeta] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["achievements", pagination],
    queryFn: async () => {
      const response = await customAxios.get(`achievements?page=${pagination}`);

      return response?.data || null;
    },
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true, // Keeps current table data visible while fetching the next page/filter
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data) {
      setMeta(data.meta);
    }
  }, [data]);

  if (isError) {
    return (
      <ErrorPage
        code="500"
        titleKey="error.500_title"
        defaultTitle="Gagal Memuat Data"
        subtitleKey="error.500_subtitle"
        defaultSubtitle="Terjadi masalah saat terhubung ke server kami. Silakan coba lagi nanti atau kembali ke"
      />
    );
  }

  return (
    <>
      <Navbar />
      <Header title={t("achievements.header")} />
      <main
        className={`${styles.paddingX} flex flex-col items-center justify-center gap-3 py-20 min-h-[600px] smd:flex-col`}
      >
        <div className="flex flex-wrap justify-center gap-3">
          {isLoading ? (
            <div className="grid place-items-center h-[30dvh]">
              <Loader />
            </div>
          ) : (
            <>
              {/* Empty state */}
              {!data?.data?.length && (
                <div className="h-[30dvh] grid place-items-center">
                  {t("achievements.no_achievements")}
                </div>
              )}

              {data?.data?.map(
                ({
                  thumbnail,
                  slug,
                  title_id,
                  title_en,
                  description_id,
                  description_en,
                  created_at,
                }) => {
                  return (
                    <div className="flex flex-col justify-start items-center w-[350px] h-[394px] sm:w-[320px]">
                      <img
                        src={thumbnail}
                        alt={currentLanguage === "en" ? title_en : title_id}
                        className="h-[230px] object-cover w-full"
                      />
                      <div className="flex flex-col items-start self-stretch justify-start gap-2 py-4">
                        <div className="flex flex-col items-start self-stretch gap-2">
                          <Link
                            to={slug}
                            className={`${styles.headingS} text-start text-neutral-90 line-clamp-3 hover:underline`}
                          >
                            {currentLanguage === "en" ? title_en : title_id}
                          </Link>
                          <p
                            className={`${styles.textSRegular} text-start text-neutral-80 w-full self-stretch whitespace-nowrap truncate`}
                          >
                            {currentLanguage === "en"
                              ? description_en
                              : description_id}
                          </p>
                        </div>
                        <p
                          className={`${styles.textSMedium} text-start text-primary-40`}
                        >
                          {formatDate(created_at)}
                        </p>
                      </div>
                    </div>
                  );
                },
              )}

              {/* Pagination Control */}
              {!!data?.data?.length && (
                <div
                  className={`flex items-center gap-6 justify-center w-full sticky left-0 sm:flex-col`}
                >
                  <div className="flex gap-2">
                    <button
                      className="p-2 border rounded text-primary-60 hover:bg-primary-60 hover:text-neutral-white disabled:hidden"
                      onClick={() => setPagination(1)}
                      disabled={pagination === 1}
                    >
                      <FaChevronCircleLeft />
                    </button>
                    <button
                      className="p-2 border rounded text-primary-60 hover:bg-primary-60 hover:text-neutral-white disabled:hidden"
                      onClick={() =>
                        setPagination((previousState) => previousState - 1)
                      }
                      disabled={pagination === 1}
                    >
                      <FaChevronLeft />
                    </button>
                    <button
                      className="p-2 border rounded text-primary-60 hover:bg-primary-60 hover:text-neutral-white disabled:hidden"
                      onClick={() =>
                        setPagination((previousState) => previousState + 1)
                      }
                      disabled={pagination === meta?.last_page}
                    >
                      <FaChevronRight />
                    </button>
                    <button
                      className="p-2 border rounded text-primary-60 hover:bg-primary-60 hover:text-neutral-white disabled:hidden"
                      onClick={() => setPagination(meta?.last_page)}
                      disabled={pagination === meta?.last_page}
                    >
                      <FaChevronCircleRight />
                    </button>
                  </div>
                  <span className="flex items-center gap-1">
                    <div>Page</div>
                    <strong>
                      {pagination} of {meta?.last_page}
                    </strong>
                  </span>
                  <span className="flex items-center gap-1">
                    Go to page:
                    <input
                      type="number"
                      defaultValue={pagination}
                      onBlur={(e) => {
                        if (Number(e.target.value) > meta?.last_page)
                          setPagination(meta?.last_page);
                        else if (Number(e.target.value) <= 0) setPagination(1);
                        else {
                          const page = e.target.value
                            ? Number(e.target.value) - 1
                            : 0;
                          setPagination(page);
                        }
                      }}
                      className="w-16 p-1 border rounded"
                    />
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default PenghargaanPage;
